// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// ConvexLiveBadge — small visible proof of live websocket sync (#191).
//
// Mounted only when REACT_APP_BACKEND=convex. Reads the same core data DIRECTLY
// from Convex subscriptions and shows live counts + a pulse each time the
// server pushes an update. This is the "you can SEE it working" artifact: open
// two browsers, drag a job in one, watch this badge tick in the other with no
// polling. Purely cosmetic; renders nothing in the production (mongo) path.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

import { useContext, useEffect, useRef, useState } from "react";
import { useQuery as useConvexQuery } from "convex/react";
import { AppContext } from "./app.context";
import { ModelCxContext } from "./modelCx.context";
import { refs } from "../convex/apiRefs";
import { CONVEX_SOURCE, resolveBackendMode } from "../utility/backendConfig";

export default function ConvexLiveBadge() {
  const { companyCode, locationFilter } = useContext(AppContext);
  const { tsUserDocument } = useContext(ModelCxContext);
  // A→B (#388): the Scheduler reads below are source-less (the BOX is the env).
  // `source` survives for the badge's own label AND for `sync.getSyncMeta`, which
  // is deliberately still source-based (it's replication/admin tooling, not a
  // Scheduler read) — see convex/sync.ts.
  const source = CONVEX_SOURCE;
  const mode = resolveBackendMode(tsUserDocument);
  // Only show the badge for users actually on the experiment.
  const active = mode === "convexOnly" || mode === "both";
  const hasLoc = active && !!companyCode && !!locationFilter;
  const skip = "skip";

  const projectsWithShared = useConvexQuery(
    refs.getProjectsByCompanyWithShared,
    hasLoc ? { companyCode, location: locationFilter } : skip
  );
  const candidates = useConvexQuery(
    refs.getCandidatesByCompany,
    hasLoc ? { companyCode, location: locationFilter } : skip
  );
  const syncMeta = useConvexQuery(refs.getSyncMeta, active ? { source } : skip);

  const [pulse, setPulse] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const lastRef = useRef("");
  // Draggable position. null = default (bottom-right, clear of the left-side
  // Suppressed / Created-Verified tabs). Once dragged, {x,y} pins top-left.
  const [pos, setPos] = useState(null);
  const movedRef = useRef(false);

  const projCount = projectsWithShared?.projects?.length ?? null;
  const candCount = Array.isArray(candidates) ? candidates.length : null;

  // pulse whenever a pushed update changes the counts
  useEffect(() => {
    const sig = `${projCount}|${candCount}`;
    if (lastRef.current && lastRef.current !== sig) {
      setPulse(true);
      const t = setTimeout(() => setPulse(false), 900);
      return () => clearTimeout(t);
    }
    lastRef.current = sig;
  }, [projCount, candCount]);

  const aqMeta = Array.isArray(syncMeta)
    ? syncMeta.find((m) => m.collectionName === "activeQueue")
    : null;
  const lastSync = aqMeta?.lastSyncFinishedMs
    ? new Date(aqMeta.lastSyncFinishedMs).toLocaleTimeString()
    : "—";

  // mongoOnly users (or Convex not configured): show nothing.
  if (!active) return null;

  // Drag to move. Distinguishes a click (collapse/expand) from a drag via a
  // small movement threshold so the two gestures don't fight.
  const onMouseDown = (e) => {
    const startX = e.clientX;
    const startY = e.clientY;
    const rect = e.currentTarget.getBoundingClientRect();
    const baseX = rect.left;
    const baseY = rect.top;
    movedRef.current = false;
    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) movedRef.current = true;
      setPos({ x: Math.max(0, baseX + dx), y: Math.max(0, baseY + dy) });
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };
  const handleClick = (fn) => {
    if (movedRef.current) {
      movedRef.current = false;
      return; // it was a drag, not a click — don't toggle
    }
    fn();
  };

  const placement = pos
    ? { left: pos.x, top: pos.y }
    : { right: 10, bottom: 10 };
  const box = {
    position: "fixed",
    ...placement,
    zIndex: 99999,
    backgroundColor: pulse ? "rgba(34,197,94,0.95)" : "rgba(26,26,26,0.92)",
    color: "#fff",
    border: "1px solid " + (pulse ? "#22c55e" : "#9370DB"),
    borderRadius: 8,
    padding: "6px 10px",
    font: "11px/1.35 -apple-system, system-ui, sans-serif",
    boxShadow: "0 2px 10px rgba(0,0,0,0.4)",
    transition: "background-color 0.3s ease",
    cursor: "grab",
    userSelect: "none",
    maxWidth: 230,
  };

  if (collapsed) {
    return (
      <div style={{ ...box, padding: "4px 8px" }} onMouseDown={onMouseDown} onClick={() => handleClick(() => setCollapsed(false))} title="Convex live sync (#191) — drag to move, click to expand">
        🧪⚡ Convex
      </div>
    );
  }

  return (
    <div style={box} onMouseDown={onMouseDown} onClick={() => handleClick(() => setCollapsed(true))} title="Drag to move · click to collapse">
      <div style={{ fontWeight: "bold", color: "#a78bfa" }}>
        🧪 Convex {mode === "both" ? "compare" : "live"} {pulse ? "⚡" : "•"}
      </div>
      <div>mode: <b>{mode}</b> · source: <b>{source}</b></div>
      <div>{companyCode || "—"}/{locationFilter || "—"}</div>
      <div>
        projects: <b>{projCount ?? "…"}</b> · installers: <b>{candCount ?? "…"}</b>
      </div>
      <div style={{ opacity: 0.7 }}>last mongo→convex sync: {lastSync}</div>
      <div style={{ opacity: 0.55, fontSize: 10 }}>
        {mode === "both" ? "comparing vs Mongo (parity log)" : "websocket · no polling"} (#191)
      </div>
    </div>
  );
}
